import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Skeleton from '@mui/material/Skeleton'
import { styled } from '@mui/material/styles'
import { format } from 'date-fns'
import prettyBytes from 'pretty-bytes'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Button, EmptyState, LocaleProvider } from '@clara/ui'
import {
  FileViewer,
  FileViewerProps,
  JPGRenderer,
  PNGRenderer,
  WebPRenderer,
  BMPRenderer,
  PDFRenderer,
} from '@clara/file-viewer'
import { FormatMoney, useT } from '@clara/locale'
import { ResponseError, isEmpty, isEmptyArray, omit } from '@clara/utils'

import { useEvents } from '@/hooks/use-analytics'
import DowntimeImage from '@/assets/images/loading-error.svg'
import { useManageReimbursement } from '../stores/manage-reimbursement-store'
import { combineLoadingStates } from '../utils/combine-loading-states'
import {
  ManageReimbursementForm,
  ManageReimbursementFormProps,
} from './manage-reimbursement-form'
import { settings } from '@/config'
import { map } from '../utils/map'
import { genFilename } from '../utils/gen-filename'
import { useLabelsOptions } from '@/features/reimbursements/state/labels/use-labels-options'

const Wrapper = styled('div')(({ theme }) => ({
  width: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row-reverse',
  },
}))

const Column = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: { flex: 1 },
}))

const Content = styled(Column)(({ theme }) => ({
  overflowY: 'scroll',
  paddingInline: theme.space[6],
  paddingTop: theme.space[6],
  [theme.breakpoints.up('lg')]: {
    paddingBottom: theme.space['16'],
    flex: 4,
  },
}))

const Attachments = styled(Column)(({ theme }) => ({
  position: 'relative',
  paddingInline: theme.space[6],
  '& > div:first-of-type': {
    border: '1px solid',
    borderColor: theme.palette.v3.border.default,
  },
  [theme.breakpoints.up('lg')]: {
    marginBottom: `calc(${theme.space['16']} + 1px)`,
    paddingInline: 0,
    flex: 6,
    maxWidth: '60%',
    '& > div:first-of-type': {
      border: 'none',
    },
  },
}))

const Actions = styled('footer')(({ theme }) => ({
  background: theme.palette.v3.bg.alternative,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  bottom: 0,
  borderTop: '1px solid',
  borderTopColor: theme.palette.v3.border.default,
  paddingInline: theme.space['6'],
  paddingBlock: theme.space['3'],
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    position: 'fixed',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 3,
  },
}))

const LoadingState = () => {
  return (
    <Wrapper>
      <Content>
        <Stack spacing={4}>
          <Skeleton variant="rectangular" height={56} />
          <Stack spacing={1}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={96} />
          </Stack>
          <Skeleton variant="rectangular" height={144} />
          <Skeleton variant="rectangular" height={144} />
        </Stack>
      </Content>
      <Attachments sx={{ mb: { lg: 0 } }}>
        <Skeleton variant="rectangular" height={'100%'} />
      </Attachments>
    </Wrapper>
  )
}

export function ManageReimbursement() {
  const { t } = useT('reimbursements')
  const { metadata, reimbursement, ...store } = useManageReimbursement()
  const {
    labels,
    labelsDict,
    isLoading: labelsAreLoading,
  } = useLabelsOptions(reimbursement.value?.labels)
  const navigate = useNavigate()
  const events = useEvents()
  const { reimbursementsLabelsIsEnabled } = useFlags()

  // Merging `loadingState` from both sources to maintain a
  // unified and consistent loading indicator.
  const loadingState =
    store.strategy === 'update'
      ? combineLoadingStates(metadata.loadingState, reimbursement.loadingState)
      : metadata.loadingState
  if (loadingState === 'idle') return null
  if (loadingState === 'loading') return <LoadingState />

  // Downtime...bad times
  if (loadingState === 'error')
    return (
      <Container
        sx={{
          alignItems: 'center',
          textAlign: 'center',
          flex: 1,
          display: 'flex',
        }}
      >
        <EmptyState
          imageUrl={DowntimeImage}
          headline={t('emptystate.load.error.title')}
          tagline={t('emptystate.load.error.description')}
          primaryAction={
            <Button variant="secondary" onClick={() => navigate(0)}>
              {t('emptystate.load.error.action')}
            </Button>
          }
        />
      </Container>
    )

  const merchants = map(metadata.merchants ?? [], (merchant) => ({
    value: merchant.code,
    label: merchant.name,
    __entity__: merchant,
  }))

  const categories = map(metadata.categories ?? [], (category) => ({
    value: category.key,
    label: category.description,
  }))

  const formProps: ManageReimbursementFormProps = {
    id: 'create-reimbursement',
    // metadata
    metadata: {
      merchants,
      categories,
      labels,
      labelsDict,
      labelsAreLoading,
      reimbursementsLabelsIsEnabled,
      ...settings,
      ...metadata.options,
    },
    onAmountChange: store.updateAmount,
    onSubmit: (values) => {
      const cleaned = omit(values, ['__merchant_name__'])
      events.sendNewReimb()
      store.updateValues({
        ...cleaned,
        expenseDate: format(values.expenseDate, 'yyyy-MM-dd'),
        labels: (values.labels ?? []).map((labelUuid) => ({
          labelUUID: labelUuid,
          labelName: labelsDict[labelUuid].label,
        })),
      })
    },
    defaultValues: {
      ...(store.strategy === 'update' && store.defaultValues),
    },
  }

  const viewerProps: FileViewerProps = {
    files: store.attachments,
    maxFiles: store.maxFiles,
    uploader: true,
    maxSize: store.maxSize,
    onChange: (attachments) => store.setAttachments(attachments),
    getFilename: genFilename,
    onDelete: (index) => {
      const { strategy, attachments, dropAttachmentAt, toast, action } = store
      const activeStrategy = strategy === 'create' || action === 'resubmit'

      if (activeStrategy || attachments.length > 1) {
        dropAttachmentAt(
          index,
          () =>
            toast({
              status: 'success',
              message: t('manage.pdfviewer.onDeleteSuccess'),
            }),
          () =>
            toast({
              status: 'error',
              message: t('manage.pdfviewer.onDeleteFailed'),
            }),
        )
      } else {
        toast({
          status: 'error',
          message: t('manage.pdfviewer.onDelete.lastFile'),
        })
      }
    },
    onError: async (err) => {
      // narrowing the error
      let message: string | undefined = undefined
      // @see https://react-dropzone.js.org/#src
      // FIXME: validate if it is the correct way
      console.error(err)
      if (!isEmpty(err.fileRejections)) {
        const rejections = err.fileRejections.flatMap((rejection) =>
          rejection.errors.map((error) => {
            switch (error.code) {
              case 'file-invalid-type':
                return t('manage.pdfviewer.error.fileInvalidType', {})
              case 'file-too-large':
                return t('manage.pdfviewer.error.fileTooLarge', {
                  maxSize: prettyBytes(store.maxSize),
                })
              case 'too-many-files':
                return t('manage.pdfviewer.error.tooManyFiles', {
                  maxFiles: store.maxFiles,
                })
              default:
                return error.message
            }
          }),
        )

        // unique
        const [head, ...other] = [...new Set(rejections)]
        message = isEmptyArray(other) ? head : 'generic error'
      } else if (ResponseError.isResponseError(err)) {
        // const reason = await parseResponse(err.response)
        message = t('manage.pdfviewer.error.uploadFile')
      }

      if (message)
        store.toast({
          status: 'error',
          message,
        })
    },
    accept: {
      'image/*': ['.png', '.jpeg', '.jpg', '.bmp', '.webp'],
      'text/xml': ['.xml'],
      'application/pdf': ['.pdf'],
    },
    renderers: [
      PNGRenderer,
      JPGRenderer,
      WebPRenderer,
      BMPRenderer,
      PDFRenderer,
    ],
  }

  return (
    <React.Fragment>
      <Wrapper>
        <Content>
          <ManageReimbursementForm {...formProps}>
            <Typography
              variant="h2"
              color={(theme) => theme.palette.v3.content.high_emphasis}
            >
              {t('create.request.title')}
              <Typography display="block" variant="body1" color="inherit">
                {t('create.request.description')}
              </Typography>
            </Typography>
          </ManageReimbursementForm>
        </Content>
        <Attachments>
          <LocaleProvider
            locale={{
              fileviewer: {
                toolbar: {
                  zoomIn: t('manage.pdfviewer.toolbar.zoomIn'),
                  zoomOut: t('manage.pdfviewer.toolbar.zoomOut'),
                  download: t('manage.pdfviewer.toolbar.download'),
                  delete: t('manage.pdfviewer.toolbar.delete'),
                  thumbnail: `${t('manage.pdfviewer.toolbar.thumbnail')} `,
                  upload: t('manage.pdfviewer.toolbar.upload'),
                },
                upload: {
                  headline: t('manage.pdfviewer.upload.headline'),
                  tagline: t('manage.pdfviewer.upload.tagline'),
                  browseFiles: t('manage.pdfviewer.upload.browseFiles'),
                },
                nofiles: {
                  headline: t('manage.pdfviewer.nofiles.headline'),
                  tagline: t('manage.pdfviewer.error.fileInvalidType'),
                },
                norenderer: {
                  headline: t('manage.pdfviewer.norenderer.headline'),
                  tagline: t('manage.pdfviewer.norenderer.tagline'),
                  download: t('manage.pdfviewer.norenderer.download'),
                },
              },
            }}
          >
            <FileViewer {...viewerProps} />
          </LocaleProvider>
        </Attachments>
      </Wrapper>
      <Actions>
        <Typography
          variant="subtitle2"
          display={{ xs: 'none', lg: 'block' }}
          color={(theme) => theme.palette.v3.content.low_emphasis}
        >
          {t('create.request.total')}
          <Typography
            component={FormatMoney}
            value={store.values?.amount || 0}
            sx={(theme) => ({ paddingInline: theme.space[2] })}
          />
        </Typography>
        <Stack
          spacing={2}
          direction={{ xs: 'column', lg: 'row-reverse' }}
          sx={(theme) => ({
            width: {
              lg: theme.sizes.md,
            },
          })}
        >
          <Button
            type="submit"
            isDisabled={store.hasAttachments === false}
            form={formProps.id}
            sx={{ flex: 1 }}
          >
            {t('create.request.action.send')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              navigate('/reimbursements')
            }}
            sx={{ flex: 1 }}
          >
            {t('create.request.action.cancel')}
          </Button>
        </Stack>
      </Actions>
    </React.Fragment>
  )
}
